import {REGION} from '@/ts/utils/consts';
import type {CountryCode} from '@/ts/types/dto/sign-in-dto';

type Region = typeof REGION;

const REGION_COUNTRIES: Record<Region, CountryCode> = {EU: 'DE', US: 'US'};

// The time zones of the States below the America/ prefix, and their groups. Most of America/* is
// Canada and Latin America, so the prefix alone would say "US" for Toronto or Mexico City.
const US_AMERICA_ZONES = new Set([
  'New_York',
  'Detroit',
  'Chicago',
  'Menominee',
  'Denver',
  'Boise',
  'Phoenix',
  'Los_Angeles',
  'Anchorage',
  'Juneau',
  'Sitka',
  'Metlakatla',
  'Yakutat',
  'Nome',
  'Adak',
  'Fort_Wayne',
  'Indianapolis',
  'Louisville',
  'Knox_IN',
  'Shiprock',
]);
const US_AMERICA_GROUPS = ['Indiana/', 'Kentucky/', 'North_Dakota/'];
// Zones of the States outside America/*, and the old US/* aliases some browsers still report.
const US_OTHER_ZONES = new Set(['Pacific/Honolulu', 'Navajo']);

function isUsTimeZone(timeZone: string): boolean {
  if (timeZone.startsWith('US/') || US_OTHER_ZONES.has(timeZone)) {
    return true;
  }
  if (!timeZone.startsWith('America/')) {
    return false;
  }
  const zone = timeZone.slice('America/'.length);
  return US_AMERICA_ZONES.has(zone) || US_AMERICA_GROUPS.some(group => zone.startsWith(group));
}

/**
 * Where the reader seems to be, from the time zone their device is set to, or null when it says
 * neither. A hint, never a rule: no lookup leaves the browser, and someone travelling or with a
 * changed clock is guessed wrong, which is why it only words the pointer to the other Balm and
 * never redirects (IPD-3376).
 */
function guessRegionFromTimeZone(timeZone: string | undefined): Region | null {
  if (!timeZone) {
    return null;
  }
  if (isUsTimeZone(timeZone)) {
    return 'US';
  }
  return timeZone.startsWith('Europe/') ? 'EU' : null;
}

/**
 * The country of every account this deployment creates. The backend fixes it from its own region
 * and ignores any country a client sends (IPD-3379): one deployment per region, one country per
 * region. Before there is a profile to read the country from, this is the reader's country.
 */
const DEPLOYMENT_COUNTRY: CountryCode = REGION_COUNTRIES[REGION];

/** The region the other deployment serves. */
const OTHER_REGION: Region = REGION === 'EU' ? 'US' : 'EU';

/** Whether the reader's device looks set to the other region's time zone. */
function looksLikeOtherRegion(): boolean {
  let timeZone: string | undefined;
  try {
    ({timeZone} = Intl.DateTimeFormat().resolvedOptions());
  } catch {
    return false;
  }
  return guessRegionFromTimeZone(timeZone) === OTHER_REGION;
}

export {DEPLOYMENT_COUNTRY, OTHER_REGION, guessRegionFromTimeZone, looksLikeOtherRegion};
